/**
 * Extract `username` and `passcode` from a reset-password URL query string.
 *
 * Background: BBA's `forgotPassword` builds the reset URL by raw-interpolating
 * the contact email into the query string (no encoding), so an address like
 * `user+tag@example.com` reaches the user's inbox as
 *   .../auth/setpassword?username=user+tag@example.com&passcode=...
 * Per `application/x-www-form-urlencoded` (which `URLSearchParams` follows),
 * a literal `+` in the query value decodes to a space — turning the username
 * into `user tag@example.com`, which BBA's set-password Joi schema then
 * rejects as not a valid email.
 *
 * The fix is defensive: preserve `+` in this specific query string before
 * letting `URLSearchParams` decode percent-escapes. Safe for `username`
 * (RFC 5322 emails never contain a literal space) and `passcode` (BBA's
 * uniqid is `[0-9a-z]+`, no `+` or space possible). Companion to afy-ui
 * PR #3105 (outgoing-side encoding) — both sides of the same encoding
 * mismatch.
 *
 * @param {string} search - the `location.search` (with or without leading `?`)
 * @returns {{ username: string | null, passcode: string | null }}
 */
export function parseResetParams(search) {
  if (typeof search !== 'string' || search.length === 0) {
    return { username: null, passcode: null };
  }
  // Replace literal `+` with its percent-encoded form before parsing so
  // URLSearchParams doesn't decode it to a space. Emails / uniqid passcodes
  // never legitimately contain `+` as a space placeholder here.
  const normalized = search.replace(/\+/g, '%2B');
  const params = new URLSearchParams(normalized);
  return {
    username: params.get('username'),
    passcode: params.get('passcode'),
  };
}
